// src/components/panel/AdministrarCaja.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../styles/panel/administrarCaja.css";

const API_BASE = process.env.REACT_APP_API_URL || "";

const acjFormatCRC = (n) => {
  const num = Number(n || 0);
  return "¢" + num.toLocaleString("es-CR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const acjFormatDateTime = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleString("es-CR");
};

const acjSafe = (v, fb = "-") => {
  if (v === null || v === undefined) return fb;
  const s = String(v).trim();
  return s.length ? s : fb;
};

const AdministrarCaja = () => {
  const navigate = useNavigate();

  // ✅ Guard
  useEffect(() => {
    const t = localStorage.getItem("token");
    if (!t) navigate("/login");
  }, [navigate]);

  const acjToken = useMemo(() => localStorage.getItem("token"), []);
  const acjHeaders = useMemo(() => {
    const h = { "Content-Type": "application/json" };
    if (acjToken) h.Authorization = `Bearer ${acjToken}`;
    return h;
  }, [acjToken]);

  // UI
  const [acjLimit, setAcjLimit] = useState(10); // 10|20|50
  const [acjLoading, setAcjLoading] = useState(false);
  const [acjOrders, setAcjOrders] = useState([]);

  const [acjSelected, setAcjSelected] = useState(() => new Set()); // ids
  const [acjTargetDate, setAcjTargetDate] = useState("");
  const [acjMoveLoading, setAcjMoveLoading] = useState(false);

  const [acjMsg, setAcjMsg] = useState(null); // {type:'ok'|'err', text:''}

  const acjSelectedCount = useMemo(() => acjSelected.size, [acjSelected]);

  const acjFetchRecent = async () => {
    setAcjMsg(null);
    setAcjLoading(true);
    setAcjOrders([]);
    setAcjSelected(new Set());

    try {
      const res = await fetch(`${API_BASE}/api/admin/caja/recent?limit=${acjLimit}`, {
        method: "GET",
        headers: acjHeaders,
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || "Error obteniendo órdenes recientes.");

      const list = data?.orders || [];
      setAcjOrders(list);

      if (list.length === 0) {
        setAcjMsg({ type: "err", text: "No hay órdenes recientes para mostrar." });
      }
    } catch (e) {
      setAcjMsg({ type: "err", text: e.message || "Error obteniendo órdenes." });
    } finally {
      setAcjLoading(false);
    }
  };

  useEffect(() => {
    acjFetchRecent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acjLimit]);

  const acjToggleId = (id) => {
    setAcjSelected((prev) => {
      const next = new Set(prev);
      const key = String(id);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const acjSelectAll = () => {
    setAcjSelected((prev) => {
      if (acjOrders.length === 0) return prev;
      const all = new Set(acjOrders.map((o) => String(o.id)));
      // si ya están todos, deselecciona
      const alreadyAll = prev.size === all.size;
      return alreadyAll ? new Set() : all;
    });
  };

  const acjMoveToCaja = async () => {
    if (acjSelectedCount === 0) {
      setAcjMsg({ type: "err", text: "Selecciona al menos 1 orden para mover de caja." });
      return;
    }
    if (!acjTargetDate) {
      setAcjMsg({ type: "err", text: "Selecciona el día de la caja (fecha)." });
      return;
    }

    const ok = window.confirm(
      `⚠️ Vas a mover ${acjSelectedCount} orden(es) a la caja del día:\n\n${acjTargetDate}\n\nEsto cambiará createdAt a ${acjTargetDate} 20:59:59.`
    );
    if (!ok) return;

    setAcjMoveLoading(true);
    setAcjMsg(null);

    try {
      const orderIds = Array.from(acjSelected).map((x) => Number(x));

      const res = await fetch(`${API_BASE}/api/admin/caja/move`, {
        method: "POST",
        headers: acjHeaders,
        body: JSON.stringify({ orderIds, targetDate: acjTargetDate }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || "Error moviendo órdenes de caja.");

      setAcjMsg({ type: "ok", text: data?.message || "Órdenes movidas correctamente." });

      // Refrescar lista
      await acjFetchRecent();
      setAcjTargetDate("");
    } catch (e) {
      setAcjMsg({ type: "err", text: e.message || "Error moviendo órdenes." });
    } finally {
      setAcjMoveLoading(false);
    }
  };

  return (
    <div className="acj-wrap">
      <header className="acj-head">
        <h1 className="acj-title">Administrar Caja</h1>
        <p className="acj-subtitle">
          Selecciona órdenes recientes y muévelas a la caja del día correcto (ajusta <b>createdAt</b> a 20:59:59).
        </p>
      </header>

      <section className="acj-card">
        <div className="acj-toolbar">
          <div className="acj-left">
            <label className="acj-label">Mostrar:</label>
            <select
              className="acj-select"
              value={acjLimit}
              onChange={(e) => setAcjLimit(Number(e.target.value))}
            >
              <option value={10}>Últimas 10</option>
              <option value={20}>Últimas 20</option>
              <option value={50}>Últimas 50</option>
            </select>

            <button className="acj-btn acj-btn--ghost" type="button" onClick={acjFetchRecent} disabled={acjLoading}>
              {acjLoading ? "Actualizando..." : "Actualizar"}
            </button>
          </div>

          <div className="acj-right">
            <div className="acj-dateBox">
              <label className="acj-label">Día de caja:</label>
              <input
                className="acj-input"
                type="date"
                value={acjTargetDate}
                onChange={(e) => setAcjTargetDate(e.target.value)}
              />
            </div>

            <button
              className="acj-btn acj-btn--primary"
              type="button"
              onClick={acjMoveToCaja}
              disabled={acjMoveLoading || acjSelectedCount === 0}
              title={acjSelectedCount === 0 ? "Selecciona órdenes primero" : "Mover órdenes"}
            >
              {acjMoveLoading ? "Moviendo..." : `Mover de caja (${acjSelectedCount})`}
            </button>
          </div>
        </div>

        {acjMsg?.text && (
          <div className={acjMsg.type === "ok" ? "acj-msg acj-msg--ok" : "acj-msg acj-msg--err"}>
            {acjMsg.text}
          </div>
        )}
      </section>

      <section className="acj-results">
        <h2 className="acj-h2">Órdenes recientes</h2>

        <div className="acj-tableWrap">
          <table className="acj-table">
            <thead>
              <tr>
                <th className="acj-thCheck">
                  <button className="acj-checkAll" type="button" onClick={acjSelectAll} title="Seleccionar todo / nada">
                    ✓
                  </button>
                </th>
                <th># Orden</th>
                <th>Cliente</th>
                <th>Forma Pago</th>
                <th>Total</th>
                <th>Creada</th>
              </tr>
            </thead>

            <tbody>
              {acjOrders.length === 0 ? (
                <tr>
                  <td colSpan="6" className="acj-empty">
                    {acjLoading ? "Cargando..." : "Sin resultados"}
                  </td>
                </tr>
              ) : (
                acjOrders.map((o) => {
                  const checked = acjSelected.has(String(o.id));
                  return (
                    <tr key={o.id} className={checked ? "acj-row acj-row--selected" : "acj-row"}>
                      <td className="acj-tdCheck">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => acjToggleId(o.id)}
                        />
                      </td>
                      <td>{acjSafe(o.orderNumber)}</td>
                      <td>{acjSafe(o.customer)}</td>
                      <td>{acjSafe(o.formaPago)}</td>
                      <td>{acjFormatCRC(o.totalPayment)}</td>
                      <td>{acjFormatDateTime(o.createdAt)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default AdministrarCaja;
